import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  IGenericServiceResponse,
  IServiceResponse,
} from '../interfaces/IServiceResponse';
import { environment } from 'src/environments/environment';
import { EpisodeOfCareService } from './episodeOfCare.service';
import { PatientService } from './patient.service';
import { TranslateService } from '@ngx-translate/core';
import { SwalToastService } from './swal.service';
import { INutritionIntakeDetailed } from '../interfaces/NutritionIntake/INutritionIntakeDetailed';
import { INutritionIntakeDropdown } from '../interfaces/NutritionIntake/INutritionIntakeDropdown';
import { IAddNutritionIntake } from '../interfaces/NutritionIntake/IAddNutritionIntake';

@Injectable({ providedIn: 'root' })
export class NutritionIntakeService {
  public nutritionIntakeModalSub = new BehaviorSubject<boolean>(false);

  public nutritionIntakeEditSub = new BehaviorSubject<
    INutritionIntakeDetailed | undefined
  >(undefined);

  public nutritionIntakeDataSub = new BehaviorSubject<
    INutritionIntakeDetailed[]
  >([]);

  public nutritionIntakeLoadingSub = new BehaviorSubject<boolean>(false);
  public nutritionIntakeLoadingSub$ =
    this.nutritionIntakeLoadingSub.asObservable();

  constructor(
    private readonly _http: HttpClient,
    private readonly eocService: EpisodeOfCareService,
    private readonly patientService: PatientService,
    private readonly translate: TranslateService,
    private readonly swalToastService: SwalToastService
  ) {}

  async getData() {
    this.nutritionIntakeLoadingSub.next(true);
    this.getNutritionIntake(
      this.patientService.patientIDsignal(),
      this.eocService.currentEocId(),
      this.eocService.currentVisitId()
    ).subscribe({
      next: (response) => {
        this.nutritionIntakeDataSub.next(response?.data!);
        this.nutritionIntakeLoadingSub.next(false);
      },
      error: (err) => {
        this.swalToastService.toastError(
          'Unable to retrieve Nutrition Intake data'
        );
        console.error('Error fetching Nutrition Intake data:', err);
      },
    });
  }

  getNutritionIntake(
    patientId: string,
    episodeCareId: string,
    visitId?: string
  ): Observable<IGenericServiceResponse<INutritionIntakeDetailed[]>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this._http.get<IGenericServiceResponse<INutritionIntakeDetailed[]>>(
      `${environment.BACKEND_URL}Food/${patientId}/${episodeCareId}?visitId=${visitId}`,
      {
        headers: headers,
      }
    );
  }

  getNutritionIntakeDropdown(): Observable<
    IGenericServiceResponse<INutritionIntakeDropdown>
  > {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this._http.get<IGenericServiceResponse<INutritionIntakeDropdown>>(
      `${environment.BACKEND_URL}Food/FoodTypeDropdownData`,
      {
        headers: headers,
      }
    );
  }

  addNutritionIntake(niObj: IAddNutritionIntake) {
    niObj.visitId = this.eocService.currentVisitId();
    niObj.patientId = this.patientService.patientIDsignal();

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.post(`${environment.BACKEND_URL}Food`, niObj, {
      headers: headers,
    });
  }

  editNutritionIntake(niObj: IAddNutritionIntake) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.put(`${environment.BACKEND_URL}Food/${niObj.id}`, niObj, {
      headers: headers,
    });
  }

  deleteNutritionIntake(medicalAlertId: string): Observable<IServiceResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.delete<IServiceResponse>(
      `${environment.BACKEND_URL}Food/${medicalAlertId}`,
      {
        headers: headers,
      }
    );
  }

  deleteNutritionIntakeFromTable(nutritionIntakeId: string) {
    // REMOVE THE Nutrition Intake record
    this.nutritionIntakeDataSub.next(
      this.nutritionIntakeDataSub.value?.filter(
        (ni) => ni.id?.toString() != nutritionIntakeId
      )
    );
  }
}
